import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  MessageCircle,
  User,
  Bot,
  Clock,
  Phone,
  Mail,
  Calendar,
  ChevronRight,
  Search,
  Filter,
  RefreshCw,
  X,
  Send,
  Users,
  CheckCircle,
  AlertCircle,
  Archive
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminChatManager = () => {
  const [loading, setLoading] = useState(true);
  const [chats, setChats] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedChat, setSelectedChat] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [agentMessage, setAgentMessage] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetchChats();
  }, [statusFilter]);

  // Auto-refresh every 15 seconds
  useEffect(() => {
    const interval = setInterval(fetchChats, 15000);
    return () => clearInterval(interval);
  }, [statusFilter]);

  const fetchChats = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      const params = new URLSearchParams();
      if (statusFilter) params.append("status", statusFilter);
      params.append("limit", "100");

      const response = await axios.get(`${API}/admin/chats/all?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setChats(response.data.chats || []);
      setStats(response.data.stats || null);
    } catch (error) {
      console.error("Failed to fetch chats:", error);
      toast.error("Failed to load chats");
    } finally {
      setLoading(false);
    }
  };

  const handleViewChat = async (chat) => {
    setSelectedChat(chat);
    try {
      const token = localStorage.getItem("admin_token");
      const response = await axios.get(`${API}/chat/${chat.id}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChatMessages(response.data.messages || []);
    } catch (error) {
      toast.error("Failed to load chat messages");
    }
  };

  const handleTakeoverChat = async (chatId) => {
    try {
      const token = localStorage.getItem("admin_token");
      await axios.post(`${API}/admin/chats/${chatId}/takeover`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Chat taken over successfully!");
      fetchChats();
      if (selectedChat?.id === chatId) {
        setSelectedChat(prev => ({ ...prev, status: "human" }));
      }
    } catch (error) {
      toast.error("Failed to takeover chat");
    }
  };

  const handleSendAgentMessage = async () => {
    if (!agentMessage.trim() || !selectedChat) return;

    setSendingMessage(true);
    try {
      const token = localStorage.getItem("admin_token");
      await axios.post(
        `${API}/admin/chats/${selectedChat.id}/send?message=${encodeURIComponent(agentMessage)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAgentMessage("");
      handleViewChat(selectedChat);
    } catch (error) {
      toast.error("Failed to send message");
    } finally {
      setSendingMessage(false);
    }
  };

  const handleCloseChat = async (chatId) => {
    try {
      const token = localStorage.getItem("admin_token");
      await axios.post(`${API}/admin/chats/${chatId}/close`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Chat closed");
      setSelectedChat(null);
      fetchChats();
    } catch (error) {
      toast.error("Failed to close chat");
    }
  };

  const formatDateTime = (isoStr) => {
    if (!isoStr) return "-";
    const date = new Date(isoStr);
    return date.toLocaleString("en-IN", {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      bot: "bg-gray-100 text-gray-700",
      human: "bg-blue-100 text-blue-700",
      waiting_handover: "bg-amber-100 text-amber-700 animate-pulse",
      closed: "bg-green-100 text-green-700"
    };
    const labels = {
      bot: "Bot",
      human: "Agent",
      waiting_handover: "Waiting",
      closed: "Closed"
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || "bg-gray-100"}`}>
        {labels[status] || status}
      </span>
    );
  };

  const filteredChats = chats.filter(chat => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      (chat.user_name || "").toLowerCase().includes(search) ||
      (chat.user_phone || "").toLowerCase().includes(search) ||
      (chat.user_email || "").toLowerCase().includes(search) ||
      (chat.session_id || "").toLowerCase().includes(search)
    );
  });

  return (
    <AdminLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <MessageCircle className="w-6 h-6 text-[#B08968]" />
              Chat Manager
            </h1>
            <p className="text-gray-600 mt-1">
              View and manage all customer conversations
            </p>
          </div>
          <Button
            variant="outline"
            onClick={fetchChats}
            className="gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg border p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gray-100 rounded-lg">
                  <MessageCircle className="w-5 h-5 text-gray-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
                  <p className="text-sm text-gray-500">Total Chats</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg border p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-blue-600">{stats.active}</p>
                  <p className="text-sm text-gray-500">Active</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg border p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <AlertCircle className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-amber-600">{stats.waiting_handover}</p>
                  <p className="text-sm text-gray-500">Waiting for Agent</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg border p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-600">{stats.closed}</p>
                  <p className="text-sm text-gray-500">Closed</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search by name, phone, email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border rounded-lg px-3 py-2 text-sm bg-white"
          >
            <option value="">All Status</option>
            <option value="bot">Bot</option>
            <option value="human">Agent</option>
            <option value="waiting_handover">Waiting for Agent</option>
            <option value="closed">Closed</option>
          </select>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Chat List */}
          <div className="lg:col-span-1 bg-white rounded-xl border overflow-hidden">
            <div className="p-4 border-b bg-gray-50">
              <h3 className="font-medium text-gray-900">
                Conversations ({filteredChats.length})
              </h3>
            </div>
            <div className="divide-y max-h-[600px] overflow-y-auto">
              {loading ? (
                <div className="p-8 text-center text-gray-500">
                  <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                  Loading chats...
                </div>
              ) : filteredChats.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <MessageCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No chats found</p>
                </div>
              ) : (
                filteredChats.map((chat) => (
                  <div
                    key={chat.id}
                    onClick={() => handleViewChat(chat)}
                    className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                      selectedChat?.id === chat.id ? "bg-[#B08968]/5 border-l-4 border-l-[#B08968]" : ""
                    } ${chat.status === "waiting_handover" ? "bg-amber-50" : ""}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          chat.user_name ? "bg-[#B08968]/10" : "bg-gray-100"
                        }`}>
                          {chat.user_name ? (
                            <span className="text-sm font-medium text-[#B08968]">
                              {chat.user_name.charAt(0).toUpperCase()}
                            </span>
                          ) : (
                            <User className="w-4 h-4 text-gray-400" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-sm text-gray-900">
                            {chat.user_name || "Visitor"}
                          </p>
                          {chat.user_phone && (
                            <p className="text-xs text-gray-500">{chat.user_phone}</p>
                          )}
                        </div>
                      </div>
                      {getStatusBadge(chat.status)}
                    </div>
                    
                    {chat.last_message && (
                      <p className="text-sm text-gray-600 truncate mb-2">
                        <span className="text-gray-400">
                          {chat.last_message.sender === "user" ? "User: " : 
                           chat.last_message.sender === "agent" ? "Agent: " : "Bot: "}
                        </span>
                        {chat.last_message.message}
                      </p>
                    )}
                    
                    <div className="flex items-center justify-between text-xs text-gray-400">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatDateTime(chat.last_message_at)}
                      </span>
                      <span>{chat.message_count || 0} messages</span>
                    </div>

                    {/* Requirements badges */}
                    {chat.requirements && Object.values(chat.requirements).some(v => v) && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {chat.requirements.guest_count && (
                          <span className="text-xs bg-gray-100 px-2 py-0.5 rounded">
                            👥 {chat.requirements.guest_count}
                          </span>
                        )}
                        {chat.requirements.budget_range && (
                          <span className="text-xs bg-gray-100 px-2 py-0.5 rounded">
                            💰 {chat.requirements.budget_range}
                          </span>
                        )}
                        {chat.requirements.celebration_type && (
                          <span className="text-xs bg-gray-100 px-2 py-0.5 rounded">
                            🎉 {chat.requirements.celebration_type}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Chat Detail */}
          <div className="lg:col-span-2 bg-white rounded-xl border overflow-hidden">
            {selectedChat ? (
              <div className="flex flex-col h-[600px]">
                {/* Chat Header */}
                <div className="p-4 border-b bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#B08968]/10 flex items-center justify-center">
                        {selectedChat.user_name ? (
                          <span className="text-lg font-medium text-[#B08968]">
                            {selectedChat.user_name.charAt(0).toUpperCase()}
                          </span>
                        ) : (
                          <User className="w-5 h-5 text-[#B08968]" />
                        )}
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900">
                          {selectedChat.user_name || "Visitor"}
                        </h3>
                        <div className="flex items-center gap-3 text-sm text-gray-500">
                          {selectedChat.user_phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {selectedChat.user_phone}
                            </span>
                          )}
                          {selectedChat.user_email && (
                            <span className="flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {selectedChat.user_email}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusBadge(selectedChat.status)}
                      {selectedChat.status !== "human" && selectedChat.status !== "closed" && (
                        <Button
                          size="sm"
                          className="bg-[#B08968] hover:bg-[#9A7858]"
                          onClick={() => handleTakeoverChat(selectedChat.id)}
                        >
                          Take Over
                        </Button>
                      )}
                      {selectedChat.status !== "closed" && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 border-red-200 hover:bg-red-50"
                          onClick={() => handleCloseChat(selectedChat.id)}
                        >
                          Close
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Requirements Summary */}
                  {selectedChat.requirements && Object.values(selectedChat.requirements).some(v => v) && (
                    <div className="mt-3 p-3 bg-amber-50 rounded-lg">
                      <p className="text-xs font-medium text-amber-800 mb-2">Captured Requirements:</p>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm text-amber-700">
                        {selectedChat.requirements.guest_count && (
                          <span>👥 Guests: {selectedChat.requirements.guest_count}</span>
                        )}
                        {selectedChat.requirements.preferred_dates && (
                          <span>📅 Dates: {selectedChat.requirements.preferred_dates}</span>
                        )}
                        {selectedChat.requirements.budget_range && (
                          <span>💰 Budget: {selectedChat.requirements.budget_range}</span>
                        )}
                        {selectedChat.requirements.stay_duration && (
                          <span>🏠 Duration: {selectedChat.requirements.stay_duration}</span>
                        )}
                        {selectedChat.requirements.celebration_type && (
                          <span>🎉 Event: {selectedChat.requirements.celebration_type}</span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Intent Data */}
                  {selectedChat.intent && (
                    <div className="mt-2 flex items-center gap-2 text-xs">
                      <span className="text-gray-500">Intent:</span>
                      <span className={`px-2 py-0.5 rounded-full ${
                        selectedChat.intent.primary_intent === "ready_to_book"
                          ? "bg-green-100 text-green-700"
                          : "bg-gray-100 text-gray-600"
                      }`}>
                        {selectedChat.intent.primary_intent}
                      </span>
                      {selectedChat.intent.high_intent_ready > 50 && (
                        <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                          High Intent ({selectedChat.intent.high_intent_ready}%)
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
                  {chatMessages.map((msg, idx) => (
                    <div
                      key={msg.id || idx}
                      className={`flex ${msg.sender === "user" ? "justify-end" : "justify-start"}`}
                    >
                      <div className={`max-w-[70%] rounded-2xl px-4 py-2.5 ${
                        msg.sender === "user"
                          ? "bg-[#B08968] text-white rounded-br-md"
                          : msg.sender === "agent"
                          ? "bg-blue-500 text-white rounded-bl-md"
                          : "bg-white text-gray-800 shadow-sm rounded-bl-md border"
                      }`}>
                        <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                        <p className={`text-xs mt-1 ${
                          msg.sender === "user" || msg.sender === "agent" 
                            ? "text-white/70" 
                            : "text-gray-400"
                        }`}>
                          {formatDateTime(msg.timestamp)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Agent Input */}
                {selectedChat.status === "human" && (
                  <div className="p-4 border-t bg-white">
                    <div className="flex gap-2">
                      <Input
                        value={agentMessage}
                        onChange={(e) => setAgentMessage(e.target.value)}
                        onKeyPress={(e) => e.key === "Enter" && handleSendAgentMessage()}
                        placeholder="Type your reply..."
                        className="flex-1"
                      />
                      <Button
                        className="bg-[#B08968] hover:bg-[#9A7858]"
                        onClick={handleSendAgentMessage}
                        disabled={sendingMessage || !agentMessage.trim()}
                      >
                        <Send className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}

                {/* Closed Chat Notice */}
                {selectedChat.status === "closed" && (
                  <div className="p-4 border-t bg-gray-100 text-center text-gray-500">
                    <Archive className="w-5 h-5 mx-auto mb-1" />
                    <p className="text-sm">This chat has been closed</p>
                    {selectedChat.ended_at && (
                      <p className="text-xs">Closed on {formatDateTime(selectedChat.ended_at)}</p>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="h-[600px] flex items-center justify-center text-gray-500">
                <div className="text-center">
                  <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p className="text-lg font-medium">Select a conversation</p>
                  <p className="text-sm">Choose a chat from the list to view details</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminChatManager;
